import * as React from 'react';
import { useEffect } from 'react';

import { useAtomValue, useSetAtom } from 'jotai';

import { translate } from '~l10n/client';
import { toast } from '~shared/components/Toast';
import {
  createNoOpHandler,
  createUploadToS3Handler,
} from '~teacher/components/FileDrop/FileHandlers';
import { FileHandlerConfigBuilder } from '~teacher/components/FileDrop/FileHandlers/FileHandlerConfig';
import { useFileDropConfig } from '~teacher/components/FileDrop/hooks/useFileDropConfig';
import {
  GenerateQuestionStep,
  shouldOpenAtStep2Atom,
  useScopedStepAtom,
} from '~teacher/components/QuizEditor/GenerateQuestionDialog/atom';
import { proCheck } from '~teacher/util/proCheck';

import { assertUnreachable } from '../../../../util/assertUnreachable';
import { useQuestionGenerator } from './context';
import { GenerateQuestionStep1 } from './GenerateQuestionStep1';
import { GenerateQuestionStep2 } from './GenerateQuestionStep2';

const MAX_FILES = 1;
const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB

interface Props {
  onClose: () => void;
  refreshQuestions: () => Promise<void>;
  resetRef?: React.MutableRefObject<(() => void) | null>;
}
export function GenerateQuestion({
  onClose,
  refreshQuestions,
  resetRef,
}: Props) {
  const [currentStep, setCurrentStep] = useScopedStepAtom();
  const shouldOpenAtStep2 = useAtomValue(shouldOpenAtStep2Atom);
  const setShouldOpenAtStep2 = useSetAtom(shouldOpenAtStep2Atom);
  const questionGenerator = useQuestionGenerator();

  // MIME types are optional but recommended for more robust file type detection
  const fileHandlerConfig = useFileHandlerConfig();

  const fileDropConfig = useFileDropConfig({
    maxFileCount: MAX_FILES,
    maxFileSize: MAX_FILE_SIZE,
    ...fileHandlerConfig,
  });

  useEffect(() => {
    if (resetRef) {
      resetRef.current = () => questionGenerator.reset();
    }
  }, [resetRef, questionGenerator]);

  // Open step 2 when user click success toast link
  useEffect(() => {
    if (shouldOpenAtStep2) {
      setCurrentStep({ type: 'SetStep', step: GenerateQuestionStep.Questions });
      // Reset stale data
      questionGenerator.reset();
      // Reset the atom for next generation
      setShouldOpenAtStep2(false);
    }
  }, [
    shouldOpenAtStep2,
    setCurrentStep,
    setShouldOpenAtStep2,
    questionGenerator,
  ]);

  switch (currentStep) {
    case GenerateQuestionStep.Settings:
      return <GenerateQuestionStep1 {...fileDropConfig} />;
    case GenerateQuestionStep.Questions:
      return (
        <GenerateQuestionStep2
          onClose={onClose}
          refreshQuestions={refreshQuestions}
        />
      );
    default:
      assertUnreachable(currentStep);
  }
}

function useFileHandlerConfig() {
  const isPro = proCheck.isPro();

  const onError = () => {
    toast.critical(translate('Failed to upload file'));
  };

  // Free users: use no-op handler to "mock" file upload in the UI without any API call
  // Pro users: use real S3 upload
  // Paywall check happens when clicking the Generate button
  const handler = isPro
    ? createUploadToS3Handler({ onError })
    : createNoOpHandler();

  return new FileHandlerConfigBuilder()
    .addHandlerForGroups(handler, [
      { extensions: ['pdf'], mimeTypes: ['application/pdf'] },
      { extensions: ['csv'], mimeTypes: ['text/csv'] },
      { extensions: ['jpg', 'jpeg'], mimeTypes: ['image/jpeg'] },
      { extensions: ['png'], mimeTypes: ['image/png'] },
    ])
    .build();
}
